import { createSlice, type PayloadAction } from '@reduxjs/toolkit';

import type {
  Register,
  RegisterMeldeFeilRequest,
  SendRegisterInnsynForesporselRequest,
  RegisterDialog,
  RegisterDialogData,
  RegisterDialogRequest,
} from '../../../types/helsenorge.helseregistre.internalapi.entities.entities';
import type { RegisterDialogType } from '../../../types/helsenorge.helseregistre.internalapi.entities.enums';
import type { AppDispatch } from '../../store';

import { track } from '@helsenorge/framework-utils/amplitude';
import { UtfortHandling } from '@helsenorge/framework-utils/amplitude/ampli';
import { post } from '@helsenorge/framework-utils/hn-proxy-service';

import { fetchRegisterDialogTyper, REMOVE_KEY } from '../register-dialog-types';

export enum loadingstate {
  started = 0,
  inProgess = 1,
  done = 2,
  error = 3,
}

export interface RegisterMeldingState {
  sendingMeldeFeil: boolean;
  sentMeldeFeil: boolean;
  meldeFeilError: boolean;
  meldeFeilRegister: Register | null;
  meldeFeilControlHash: string | null;
  processingRegisterId: string | null;
  loading: boolean;
  innsendingStatus?: loadingstate;
  innsynForesporselError: boolean;
}

export const initialState: RegisterMeldingState = {
  sendingMeldeFeil: false,
  sentMeldeFeil: false,
  meldeFeilError: false,
  meldeFeilRegister: null,
  meldeFeilControlHash: null,
  loading: false,
  innsendingStatus: loadingstate.started,
  processingRegisterId: null,
  innsynForesporselError: false,
};

const meldingSlice = createSlice({
  name: 'registerMelding',
  initialState,
  reducers: {
    REGISTER_INNSYN_RESET(state: RegisterMeldingState) {
      state.innsynForesporselError = false;
      state.innsendingStatus = loadingstate.started;
      state.processingRegisterId = null;
    },
    REGISTER_INNSYN_ERROR(state: RegisterMeldingState) {
      state.loading = false;
      state.innsynForesporselError = true;
      state.innsendingStatus = loadingstate.error;
    },
    REGISTER_INNSYN_LOADING(state: RegisterMeldingState, action: PayloadAction<string | null>) {
      state.loading = true;
      state.innsendingStatus = loadingstate.inProgess;
      state.processingRegisterId = action.payload;
    },
    REGISTER_INNSYN_SUCCESS(state: RegisterMeldingState) {
      state.loading = false;
      state.innsendingStatus = loadingstate.done;
    },
    SUBMIT_MESSAGE_SET_REGISTER(state: RegisterMeldingState, action: PayloadAction<Register | null>) {
      state.meldeFeilRegister = action.payload;
    },
    SUBMIT_MESSAGE_SET_CONTROLHASH(state: RegisterMeldingState, action: PayloadAction<string | null>) {
      state.meldeFeilControlHash = action.payload;
    },
    SUBMIT_MESSAGE(state: RegisterMeldingState) {
      state.sendingMeldeFeil = true;
    },
    SUBMIT_MESSAGE_SUCCESS(state: RegisterMeldingState) {
      state.sendingMeldeFeil = false;
      state.sentMeldeFeil = true;
    },
    SUBMIT_MESSAGE_FAIL(state: RegisterMeldingState) {
      state.sendingMeldeFeil = false;
      state.meldeFeilError = true;
    },
    SUBMIT_MESSAGE_DISMISS(state: RegisterMeldingState) {
      state.meldeFeilError = false;
      state.sentMeldeFeil = false;
    },
  },
});

export const {
  REGISTER_INNSYN_RESET,
  REGISTER_INNSYN_ERROR,
  REGISTER_INNSYN_LOADING,
  REGISTER_INNSYN_SUCCESS,
  SUBMIT_MESSAGE,
  SUBMIT_MESSAGE_SUCCESS,
  SUBMIT_MESSAGE_FAIL,
  SUBMIT_MESSAGE_DISMISS,
  SUBMIT_MESSAGE_SET_REGISTER,
  SUBMIT_MESSAGE_SET_CONTROLHASH,
} = meldingSlice.actions;

export function getRegisterDialogData(
  registerDialog: RegisterDialog | undefined,
  dialogType: RegisterDialogType
): RegisterDialogData | undefined {
  if (registerDialog && registerDialog.dialogTypes) {
    return registerDialog.dialogTypes.find(o => o.dialogType === dialogType);
  }

  return undefined;
}

export const SendInnsynBestilling = (dialogTyper: RegisterDialogType[], register: Register, controlHash?: string | null) => {
  return (dispatch: AppDispatch): null => {
    if (register.herId) {
      const payload: SendRegisterInnsynForesporselRequest = {
        registerId: register.registerId,
        registerNavn: register.navn,
        herId: register.herId,
        dialogType: dialogTyper,
        controlHash: controlHash ?? '',
      };
      dispatch(REGISTER_INNSYN_LOADING(register.registerId));
      post('helseregistre', 'api/v1/SendRegisterInnsynForesporsel', payload)
        .then(() => {
          dispatch(REGISTER_INNSYN_SUCCESS());
          if (register.navn) {
            track(new UtfortHandling({ handling: 'bestille', objekt: 'registerinnsyn', etikett: register.navn }));
          }
          if (register.herId && register.registerId) {
            const arg: RegisterDialogRequest = {
              herId: register.herId,
              registerId: register.registerId,
            };
            dispatch(REMOVE_KEY(arg));
            dispatch(fetchRegisterDialogTyper(arg));
          }
        })
        .catch(() => {
          dispatch(REGISTER_INNSYN_ERROR());
          if (register.herId && register.registerId) {
            const arg: RegisterDialogRequest = {
              herId: register.herId,
              registerId: register.registerId,
            };
            dispatch(REMOVE_KEY(arg));
            dispatch(fetchRegisterDialogTyper(arg));
          }
        });
    }

    return null;
  };
};

export const submitMessage = (message: string, register: Register, controlHash: string) => {
  return (dispatch: AppDispatch): null => {
    dispatch(SUBMIT_MESSAGE());

    if (register.herId && register.databehandlingsansvarlig && register.navn && register?.registerId) {
      const payload: RegisterMeldeFeilRequest = {
        meldingTekst: message,
        herId: register.herId,
        registerId: register.registerId,
        registerNavn: register.navn,
        controlHash: controlHash,
      };

      post('helseregistre', 'api/v1/RegisterMeldeFeil', payload)
        .then(() => {
          dispatch(SUBMIT_MESSAGE_SUCCESS());
        })
        .catch(() => {
          dispatch(SUBMIT_MESSAGE_FAIL());
        });
    }
    return null;
  };
};

export default meldingSlice.reducer;
